/**
 * LooksApiService — appel API AWS Looks (mytenue-aws-cdk).
 *
 * Remplace les calculs locaux de Looks AI / Sélection styliste par un
 * GET vers le cache pré-calculé (DynamoDB LooksCache via Lambda Reader).
 *
 * Architecture cible (cf. mytenue-aws-cdk/docs/ARCHITECTURE.md) :
 *
 *   App ──GET──► HTTP API Gateway v2 ──► Lambda Reader ──► DynamoDB LooksCache
 *                                                              │
 *                       1 GetItem · < 100ms p99 ◄──────────────┘
 *
 *   Cache MISS → réponse 202, l'app re-poll dans 2-3s (le Worker calcule en async)
 *   Cache HIT  → réponse 200 avec les looks
 *
 * Feature flag : `USE_BACKEND_LOOKS` (à activer progressivement).
 *
 * v1.12.388 — création du service. Pas encore wiré en prod (cf. HomeScreen flag).
 */

import { AWS_CONFIG } from '../config/aws_config';
import * as SecureStore from '../utils/secureStore';

const API_BASE = AWS_CONFIG.looksApiUrl;

// v1.12.388 — debug load (s'affiche à l'import du module = au démarrage de l'app)
console.log('[LooksAPI] module loaded · API_BASE =', API_BASE);

// ─── Feature flag ──────────────────────────────────────────────────
// Bascule à `true` une fois testé en local. Reste à `false` par défaut
// → l'app continue à calculer côté client (zéro risque).
// TODO: à valider avec AHG : flag persistant via AsyncStorage ou via remote config ?
export const USE_BACKEND_LOOKS = true;

// Mapping items LITE de l'API → format attendu par HomeScreen.stylistOutfitsGen
// (les composants utilisent it.image, it.image_url, it.id, it.name, it.brand,
//  it.category, it.price, it.affiliate_url, it.partner).
export function adaptApiLookToStylistOutfit(apiLook, wardrobeById = null) {
  const lookId = apiLook.lookId || `api_${Math.random().toString(36).slice(2, 8)}`;
  // Cache backend stocke juste itemIds. On reconstruit les items via lookup
  // dans le wardrobe local (Map id → item) si fourni.
  let items = [];
  if (Array.isArray(apiLook.itemIds) && apiLook.itemIds.length > 0 && wardrobeById) {
    items = apiLook.itemIds.map(id => wardrobeById.get(id)).filter(Boolean);
  } else if (Array.isArray(apiLook.items)) {
    // Fallback : items LITE inline (anciens caches)
    items = apiLook.items.map((it, idx) => ({
      id:           it.product_id || it.id || `${lookId}_${idx}_${(it.category || 'x').toLowerCase()}`,
      product_id:   it.product_id || it.id || null,
      name:         it.name || '',
      brand:        it.brand || '',
      category:     it.category || '',
      image:        it.photoUrl || '',
      image_url:    it.photoUrl || '',
      price:        Number(it.price) || 0,
      partner:      it.partner || null,
      affiliateUrl: it.affiliateUrl || null,
      affiliate_url:it.affiliateUrl || null,
      ai_metadata:  it.ai_metadata || {},
    }));
  }
  // tagMatches RÉELS calculés par OutfitEngine.js (depuis les styleTags items
  // matchés contre candidateTags). Permet aux onglets dynamiques + dominantTags
  // d'avoir les vraies valeurs au lieu d'un fallback ALL_TAGS_PASS.
  const tagMatches = apiLook.tagMatches && typeof apiLook.tagMatches === 'object'
    ? apiLook.tagMatches
    : {};
  // dominantTags : top 3 tags ≥ 0.85 (threshold haussé pour éviter d'afficher
  // des badges "minimaliste/sport/streetwear" sur un look chic principalement).
  const dominantTags = Object.entries(tagMatches)
    .filter(([, s]) => typeof s === 'number' && s >= 0.85)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([t]) => t);
  // bestTag : forcé en string (sinon crash OutfitCard tag.toUpperCase)
  let bestTag = null;
  if (typeof apiLook.bestTag === 'string' && apiLook.bestTag) {
    bestTag = apiLook.bestTag;
  } else if (typeof apiLook.breakdown?.style === 'string' && apiLook.breakdown.style) {
    bestTag = apiLook.breakdown.style;
  } else if (dominantTags.length > 0) {
    bestTag = dominantTags[0];
  }
  return {
    id: lookId,
    items,
    // v1.12.863 — Préserver itemIds pour late-bind quand catalogItems arrive après
    _rawItemIds: Array.isArray(apiLook.itemIds) ? apiLook.itemIds : null,
    _rawItemsLite: Array.isArray(apiLook.items) ? apiLook.items : null,
    score: apiLook.score ?? 0,
    trendScore: apiLook.trendScore ?? apiLook.score ?? 0,
    cohesion: apiLook.cohesion ?? { total: 0.7 },
    badges: apiLook.badges || [],
    bestTag,
    tagMatches,
    dominantTags,
    _source: 'backend_aws', // debug : trace l'origine
    // StyleBank enrichment (parité local : badge "Inspiration de la semaine")
    _inspiredByBank: !!apiLook._inspiredByBank,
    _bankCombination: apiLook._bankCombination || null,
  };
}

// v1.12.863 — Late-bind items depuis catalogItems quand l'outfit a été fetch
// avant que le catalog soit chargé. Résout _rawItemIds via le catalog actuel.
export function resolveStylistOutfitItems(outfit, catalogItems) {
  if (!outfit) return outfit;
  if (Array.isArray(outfit.items) && outfit.items.length > 0) return outfit;
  if (!Array.isArray(outfit._rawItemIds) || outfit._rawItemIds.length === 0) return outfit;
  if (!Array.isArray(catalogItems) || catalogItems.length === 0) return outfit;
  const byId = new Map();
  for (const it of catalogItems) {
    if (it?.id) byId.set(it.id, it);
    if (it?.product_id) byId.set(it.product_id, it);
  }
  const resolved = outfit._rawItemIds.map(id => byId.get(id)).filter(Boolean);
  if (resolved.length === 0) return outfit;
  return { ...outfit, items: resolved };
}

// ─── Helpers ────────────────────────────────────────────────────────
async function getAuthToken() {
  try {
    return await SecureStore.getItemAsync('outfit_access');
  } catch {
    return null;
  }
}

function buildQueryString(params) {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) qs.set(key, value.join(','));
    } else {
      qs.set(key, String(value));
    }
  }
  return qs.toString();
}

// ─── fetchLooksAI ──────────────────────────────────────────────────
/**
 * Récupère les Looks AI per-user (depuis le dressing de l'user logged in).
 *
 * Requiert un token JWT Cognito (sub identifie l'user).
 *
 * Retour :
 *   - Cache HIT  : { looks: [...], total, cache_hit: true, is_stale, generatedAt, nextCursor }
 *   - Cache MISS : { looks: [], computing: true, cache_hit: false }
 *
 * @param {object} opts
 * @param {number} [opts.limit=12]
 * @param {number} [opts.cursor=0]
 * @returns {Promise<object>}
 */
// v1.12.483 — POST /looks/invalidate : force recompute du cache user# côté backend.
// Appelé après ajout/suppression d'article pour avoir des outfits frais rapidement.
export async function invalidateLooksAICache() {
  if (!API_BASE) throw new Error('looksApiUrl manquant dans aws_config.js');
  const token = await getAuthToken();
  if (!token) return { ok: false, reason: 'no_token' };
  const url = `${API_BASE}/looks/invalidate`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      console.warn(`[LooksAPI] invalidate failed (${res.status}):`, errBody.slice(0, 150));
      return { ok: false, status: res.status };
    }
    // v1.13.64 — PRÉ-CHAUFFAGE : /looks/invalidate ne fait que SUPPRIMER le cache
    // (le compute ne démarrait qu'à l'ouverture de LooksScreen → 2min à froid).
    // On déclenche donc IMMÉDIATEMENT un GET /looks?type=ai (fire-and-forget) :
    // le reader voit MISS → enqueue le compute SQS → le worker calcule EN
    // ARRIÈRE-PLAN pendant que l'user navigue. Le compute worker est le même quel
    // que soit `limit`, donc limit=1 suffit à réchauffer tout le cache.
    try {
      const warmUrl = `${API_BASE}/looks?${buildQueryString({ type: 'ai', limit: 1 })}`;
      fetch(warmUrl, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      console.log('[LooksAPI] 🔥 warm compute enqueued after invalidate');
    } catch {}
    return { ok: true };
  } catch (e) {
    console.warn('[LooksAPI] invalidate exception:', e?.message);
    return { ok: false, error: e?.message };
  }
}

export async function fetchLooksAI({ limit = 12, cursor = 0, tempC = null } = {}) {
  if (!API_BASE) throw new Error('looksApiUrl manquant dans aws_config.js');
  const token = await getAuthToken();
  if (!token) {
    return { looks: [], cache_hit: false, computing: false, total: 0 };
  }

  const qs = buildQueryString({ type: 'ai', limit, cursor, tempC });
  const url = `${API_BASE}/looks?${qs}`;

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 202) {
    // Cache MISS — le Worker calcule en arrière-plan
    return { looks: [], cache_hit: false, computing: true, total: 0 };
  }

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`fetchLooksAI failed (${res.status}): ${errBody.slice(0, 200)}`);
  }

  return res.json();
}

// ─── fetchLooksStyliste ────────────────────────────────────────────
/**
 * Récupère la Sélection styliste (depuis le catalogue partenaires).
 *
 * Cache PARTAGÉ entre tous les users avec les mêmes filtres → cache hit
 * massif (2 femmes avec mêmes prefs → même cache).
 *
 * JWT optionnel (pour cohérence d'auth UX — pas nécessaire au calcul).
 *
 * @param {object} prefs
 * @param {'F'|'M'|'unisex'} prefs.gender (obligatoire)
 * @param {string} [prefs.occasion]
 * @param {string[]} [prefs.styles]
 * @param {string[]} [prefs.categories]
 * @param {string[]} [prefs.colors]
 * @param {string[]} [prefs.partners]
 * @param {string} [prefs.priceBucket]  // '0-50' | '50-100' | '100-300' | '300-500' | '500+' | 'any'
 * @param {object} [opts] { limit, cursor }
 * @returns {Promise<object>}
 */
export async function fetchLooksStyliste(prefs, { limit = 12, cursor = 0 } = {}) {
  if (!API_BASE) throw new Error('looksApiUrl manquant dans aws_config.js');
  if (!prefs?.gender) throw new Error('fetchLooksStyliste: gender requis');

  const qs = buildQueryString({
    type: 'styliste',
    gender: prefs.gender || 'F', // default F si user logged out
    occasion: prefs.occasion,
    styles: prefs.styles,
    categories: prefs.categories,
    colors: prefs.colors,
    partners: prefs.partners,
    price: prefs.priceBucket,
    limit,
    cursor,
  });
  // Route /looks-public sans JWT requirement (cache partagé, accessible logged out)
  const url = `${API_BASE}/looks-public?${qs}`;

  const headers = {};
  // JWT optionnel : si dispo on l'envoie (utile pour analytics futurs côté Lambda)
  const token = await getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, { headers });

  if (res.status === 202) {
    return { looks: [], cache_hit: false, computing: true, total: 0 };
  }

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`fetchLooksStyliste failed (${res.status}): ${errBody.slice(0, 200)}`);
  }

  return res.json();
}

// ─── fetchLooksWithRetry ────────────────────────────────────────────
/**
 * Helper : si computing=true, attend N secondes et re-fetch.
 * Le Worker calcule en arrière-plan (typiquement < 3s en env dev),
 * donc 2-3 retries suffisent.
 *
 * @param {() => Promise<object>} fetchFn
 * @param {object} opts { maxRetries=3, delayMs=2000 }
 */
export async function fetchLooksWithRetry(fetchFn, { maxRetries = 3, delayMs = 2000, backoff = false } = {}) {
  // v1.12.494 — backoff progressif optionnel : si true, le délai augmente
  // de 1.4x à chaque attempt (3000 → 4200 → 5880 → ...) pour mieux suivre
  // le temps de compute du worker AWS (~3-10s typique, jusqu'à 30s en cold).
  let currentDelay = delayMs;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const result = await fetchFn();
    if (!result.computing) return result;
    if (attempt < maxRetries - 1) {
      await new Promise((r) => setTimeout(r, currentDelay));
      if (backoff) currentDelay = Math.min(15000, Math.round(currentDelay * 1.4));
    }
  }
  // Dernier essai (peut encore retourner vide si le Worker est en panne)
  return await fetchFn();
}

// ─── healthCheck ────────────────────────────────────────────────────
/**
 * Ping le backend (sans auth, juste pour vérifier qu'il est up).
 * Utile pour activer le flag USE_BACKEND_LOOKS conditionnellement
 * via un health check au démarrage.
 */
export async function healthCheck() {
  try {
    const res = await fetch(`${API_BASE}/healthz`, { method: 'GET' });
    if (!res.ok) return { ok: false, status: res.status };
    const body = await res.json();
    return { ok: !!body.ok, ts: body.ts };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

export default {
  USE_BACKEND_LOOKS,
  fetchLooksAI,
  fetchLooksStyliste,
  fetchLooksWithRetry,
  healthCheck,
};
